import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/ProductShowcase.scss';

const FeatureIcon = ({ emoji, text }) => (
  <div className="feature-icon">
    <div className="icon-wrapper">
      <span role="img" aria-label="icon" className="feature-emoji">
        {emoji}
      </span>
    </div>
    <span>{text}</span>
  </div>
);

// Flying Bee Component - Memoized for performance
const FlyingBee = React.memo(({ index }) => {
  const beeRef = useRef(null);
  const beeConfig = useMemo(() => {
    const startDelay = index * 600;
    const flightDuration = 12000 + Math.random() * 8000;
    const flightRadius = 120 + Math.random() * 180;
    const verticalRange = 80 + Math.random() * 120;
    const startX = Math.random() * 85 + 5;
    const startY = Math.random() * 75 + 10;
    const pathType = index % 4;

    return {
      startDelay,
      flightDuration,
      flightRadius,
      verticalRange,
      startX,
      startY,
      pathType,
    };
  }, [index]);

  useEffect(() => {
    if (!beeRef.current) return;

    const bee = beeRef.current;
    bee.style.setProperty('--flight-duration', `${beeConfig.flightDuration}ms`);
    bee.style.setProperty('--flight-delay', `${beeConfig.startDelay}ms`);
    bee.style.setProperty('--flight-radius-x', `${beeConfig.flightRadius}px`);
    bee.style.setProperty('--flight-radius-y', `${beeConfig.verticalRange}px`);
    bee.style.setProperty('--start-x', `${beeConfig.startX}%`);
    bee.style.setProperty('--start-y', `${beeConfig.startY}%`);
  }, [beeConfig]);

  return (
    <div
      ref={beeRef}
      className={`flying-bee path-type-${beeConfig.pathType}`}
      style={{
        left: `${beeConfig.startX}%`,
        top: `${beeConfig.startY}%`,
      }}
    >
      <div className="bee-body">
        <span role="img" aria-label="bee" className="bee-emoji">
          🐝
        </span>
        <div className="wing wing-left"></div>
        <div className="wing wing-right"></div>
        <div className="flight-path"></div>
      </div>
    </div>
  );
});

// Honey drip component
const HoneyDrip = ({ delay, left }) => (
  <div
    className="honey-drip"
    style={{
      left: `${left}%`,
      animationDelay: `${delay}s`,
    }}
  >
    <div className="drip-body"></div>
  </div>
);

const ProductShowcase = () => {
  const showcaseRef = useRef(null);
  const [bees, setBees] = useState([]);
  const [showHoneycomb, setShowHoneycomb] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  // Default product and variation IDs from the data
  const defaultProductId = '7845920398aedd48b236fc40';
  const defaultVariationId = '7845920398aedd48b236fc41';

  const handleQuickView = () => {
    navigate(`/products?category=PURE HONEY`, {
      state: {
        selectedProductId: defaultProductId,
        selectedVariationId: defaultVariationId,
      },
    });
  };

  // Throttled mouse move for better performance
  const handleMouseMove = useCallback(
    e => {
      if (!showcaseRef.current || !isVisible) return;
      const rect = showcaseRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setMousePosition({ x, y });
    },
    [isVisible]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          showcaseRef.current.classList.add('animate-in');
          setTimeout(() => setShowHoneycomb(true), 800);

          // Reduced number of bees for better performance
          const initialBees = Array.from({ length: 4 }, (_, i) => i);
          setBees(initialBees);

          setTimeout(() => {
            setBees(prev => [
              ...prev,
              ...Array.from({ length: 2 }, (_, i) => i + prev.length),
            ]);
          }, 2500);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.1 }
    );

    if (showcaseRef.current) {
      observer.observe(showcaseRef.current);
    }

    return () => {
      if (showcaseRef.current) {
        observer.unobserve(showcaseRef.current);
      }
    };
  }, []);

  // Memoized honey drips data
  const honeyDrips = useMemo(
    () => [
      { delay: 0, left: 15 },
      { delay: 2, left: 35 },
      { delay: 4, left: 55 },
      { delay: 1.5, left: 75 },
      { delay: 3, left: 90 },
    ],
    []
  );

  // Memoized hexagon elements
  const hexagonElements = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="hexagon"
          style={{
            animationDelay: `${i * 0.1}s`,
            left: `${(i % 5) * 20}%`,
            top: `${Math.floor(i / 5) * 25}%`,
          }}
        />
      )),
    []
  );

  // Memoized honeycomb cells
  const honeycombCells = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, i) => (
        <div
          key={i}
          className="honeycomb-cell"
          style={{ animationDelay: `${i * 0.05}s` }}
        />
      )),
    []
  );

  // Memoized floating flowers
  const floatingFlowers = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flower"
          style={{ animationDelay: `${i * 1.5}s` }}
        >
          🌼
        </div>
      )),
    []
  );

  return (
    <div
      className="product-showcase honey-theme"
      ref={showcaseRef}
      onMouseMove={handleMouseMove}
    >
      {/* Animated background elements */}
      <div className="honey-background">
        <div className="honey-wave wave-1"></div>
        <div className="honey-wave wave-2"></div>
        <div className="honey-wave wave-3"></div>
      </div>

      {/* Hexagon pattern overlay */}
      <div className="hexagon-overlay">{hexagonElements}</div>

      {/* Honey drips from top */}
      <div className="honey-drips-container">
        {honeyDrips.map((drip, i) => (
          <HoneyDrip key={i} delay={drip.delay} left={drip.left} />
        ))}
      </div>

      <div className="content-container">
        <div className="content-wrapper">
          <div className="product-info">
            <div className="badge-organic">
              <span>🌿 100% Organic</span>
            </div>
            <h1 className="title">Golden Pure Honey</h1>
            <div className="category">
              <span role="img" aria-label="natural" className="category-emoji">
                🍯
              </span>
              <span>PREMIUM NATURAL FOODS</span>
            </div>
            <h2 className="product-name">Raw Organic Honey</h2>
            <p className="description">
              Experience the pure essence of nature with our 100% ORGANIC HONEY,
              carefully from wild mountain flowers by ethical beekeepers.
            </p>
            <div className="features">
              <FeatureIcon emoji="🐝" text="SUSTAINABLY" />
              <FeatureIcon emoji="🌸" text="RICH FLORAL NOTES" />
              <FeatureIcon emoji="🌱" text="CERTIFIED ORGANIC" />
              <FeatureIcon emoji="💪" text="NATURAL ANTIOXIDANTS" />
              <FeatureIcon emoji="🏆" text="AWARD WINNING" />
            </div>
            <div className="cta-section">
              <button className="shop-now" onClick={handleQuickView}>
                <span>TASTE THE DIFFERENCE</span>
                <span className="arrow">→</span>
              </button>
              <div className="trust-badges">
                <span className="badge">✓ No Additives</span>
                <span className="badge">✓ Raw & Unfiltered</span>
              </div>
            </div>
          </div>
        </div>

        <div className="image-wrapper">
          <div
            className="product-image-container"
            style={{
              transform: `translate(${(mousePosition.x - 0.5) * 20}px, ${
                (mousePosition.y - 0.5) * 20
              }px)`,
            }}
          >
            <img
              src="./WhatsApp_Image_2024-12-21_at_1.37.21_PM__1_-removebg-preview.png"
              alt="Golden Pure Honey"
              className="product-image"
            />
            <div className="honey-glow-effect"></div>
            <div className="honey-splash splash-1"></div>
            <div className="honey-splash splash-2"></div>
          </div>

          {/* Honeycomb pattern */}
          {showHoneycomb && (
            <div className="honeycomb-pattern">{honeycombCells}</div>
          )}

          {/* Flying bees */}
          <div className="flying-bees">
            {bees.map(id => (
              <FlyingBee key={id} index={id} />
            ))}
          </div>

          {/* Floating flowers */}
          <div className="floating-flowers">{floatingFlowers}</div>
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;
